import React from 'react';
import { Loader, User, Sparkles, ExternalLink, CheckCircle, Save } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations'; 

const SummariesView = ({ loading, papers, error, renderAISummary, saveArticle }) => {
  const { language } = useLanguage(); 

  return (
    <div className="space-y-6 md:space-y-8 max-w-6xl mx-auto px-4 md:px-0">
      <div className="border-b border-slate-200 pb-4 md:pb-6 mt-8 md:mt-12">
        <h2 className="text-2xl md:text-3xl font-serif text-slate-800 text-center italic tracking-tight">{t(language, 'summaries_header')}</h2>
      </div>
      
      {loading && papers.length === 0 ? (
        <div className="py-20 text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 font-medium">{t(language, 'summaries_fetching') || 'Hämtar och analyserar...'}</p>
        </div>
      ) : (
        /* CSS GRID: 1 kolumn på mobil/padda, 2 kolumner ENDAST på extra stora datorskärmar (xl) */
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6 mt-4">
          {papers.map((p, i) => (
            <div key={i} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden text-left flex flex-col transition-transform hover:scale-[1.01]">
              <div className="p-5 md:p-8 border-b border-slate-50 shrink-0">
                <div className="flex justify-between items-start mb-4">
                  <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest bg-blue-50 px-3 py-1 rounded-full">{p.topic}</span>
                </div>
                <h3 className="text-xl md:text-2xl font-serif mb-3 leading-tight text-slate-900">{p.title}</h3>
                <div className="space-y-1">
                  <p className="text-xs md:text-sm text-slate-600 font-medium italic flex items-start gap-2">
                    <User size={14} className="shrink-0 mt-0.5"/> <span className="leading-relaxed">{p.authors}</span>
                  </p>
                  <p className="text-[10px] md:text-xs text-slate-400 uppercase tracking-tighter font-sans flex items-center gap-2 truncate">
                    {p.venue} • {p.date}
                  </p>
                </div>
              </div>
              
              <div className="p-5 md:p-8 bg-slate-50/50 flex-1 flex flex-col text-left">
                <div className="flex items-center gap-2 text-[10px] font-bold text-indigo-500 uppercase tracking-widest mb-4">
                  <Sparkles size={14}/> {t(language, 'summaries_ai_analysis') || 'AI Analys'}
                </div>
                <div className="text-slate-700 leading-relaxed whitespace-pre-line text-base md:text-lg font-serif flex-1">
                  {p.aiSummary ? renderAISummary(p.aiSummary, language) : (
                    <div className="flex items-center gap-3 text-slate-400 italic font-sans text-sm">
                      <Loader size={16} className="animate-spin text-indigo-400"/> 
                      {t(language, 'summaries_processing') || 'Skapar sammanfattning...'}
                    </div>
                  )}
                </div>
                <div className="pt-4 mt-6 border-t border-slate-100 flex justify-between items-center shrink-0">
                  <a href={p.link} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-blue-600 hover:text-blue-800 uppercase flex items-center gap-1">
                    {t(language, 'inbox_read_original') || 'Läs original'} <ExternalLink size={12}/>
                  </a>
                  
                  <button
                    onClick={() => saveArticle(p)}
                    disabled={p.isSaved}
                    className={`text-[10px] md:text-[11px] font-bold uppercase flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${p.isSaved ? 'bg-green-100 text-green-700 cursor-default' : 'bg-slate-100 text-slate-600 hover:bg-blue-100 hover:text-blue-700'}`}
                  >
                    {p.isSaved ? <><CheckCircle size={14} /> {t(language, 'summaries_btn_saved')}</> : <><Save size={14} /> {t(language, 'summaries_btn_save')}</>}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <div className="p-8 bg-red-50 text-red-600 rounded-2xl text-center font-bold font-serif underline decoration-red-200">{error}</div>}
    </div>
  );
};

export default SummariesView;